import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Flame, Mail, Lock, User, Eye, EyeOff, Loader2, Sparkles, Zap, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Checkbox } from "@/components/ui/checkbox";
import { motion, AnimatePresence } from "framer-motion";
import { trackSignupCompleted } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function AuthPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuth();
  
  const [activeTab, setActiveTab] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [loginFailedEmail, setLoginFailedEmail] = useState("");

  useEffect(() => {
    const handleFocus = () => {
      setTimeout(() => {
        setIsGoogleLoading(false);
      }, 500);
    };
    
    window.addEventListener('focus', handleFocus);
    setIsGoogleLoading(false);
    
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const searchParams = new URLSearchParams(window.location.search);
  const returnTo = searchParams.get('returnTo');

  const getRedirectPath = () => {
    if (returnTo) {
      try {
        const url = new URL(decodeURIComponent(returnTo));
        if (url.origin === window.location.origin) {
          return url.pathname + url.search;
        }
      } catch {
        // Invalid URL, fall back to home
      }
    }
    return "/";
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      toast({ 
        title: "Google sign in failed", 
        description: error.message || "Could not sign in with Google.",
        variant: "destructive" 
      });
      setIsGoogleLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginFailed(false);
    setLoginFailedEmail("");
    try {
      await signIn(email, password, rememberMe);
      toast({ title: "Welcome back!", description: "You've successfully signed in." });
      window.location.href = getRedirectPath();
    } catch (error: any) {
      setLoginFailed(true);
      setLoginFailedEmail(email);
      toast({ 
        title: "Sign in failed", 
        description: error.message || "Invalid email or password.",
        variant: "destructive" 
      });
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResetLoading(true);
    try {
      await resetPassword(resetEmail);
      toast({ 
        title: "Reset email sent!", 
        description: "Check your inbox for password reset instructions."
      });
      setShowForgotPassword(false);
      setResetEmail("");
    } catch (error: any) {
      toast({ 
        title: "Reset failed", 
        description: error.message || "Could not send reset email.",
        variant: "destructive" 
      });
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await signUp(email, password, firstName, lastName);
      
      trackSignupCompleted();
      
      toast({ 
        title: "Account created!", 
        description: "Please check your email to verify your account. Your 14-day free trial starts after verification - no credit card required!"
      });
    } catch (error: any) {
      toast({ 
        title: "Sign up failed", 
        description: error.message || "Could not create account.",
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background relative overflow-x-hidden flex items-center justify-center p-4 py-8 sm:py-4">
      {/* Animated background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 sm:w-96 h-72 sm:h-96 bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo - smaller on mobile */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center mb-6 sm:mb-8"
        >
          <div className="relative mb-3 sm:mb-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-primary/30 to-primary/10 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-primary/20">
              <Flame className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            </div>
            <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-primary rounded-full animate-ping opacity-75" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
            myFit<span className="text-primary">Beats</span>
          </h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">Train smarter with rhythm</p>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card/80 backdrop-blur-xl rounded-2xl border border-border/50 shadow-xl overflow-hidden"
        >
          {/* Tab Switcher */}
          <div className="flex border-b border-border/50">
            <button
              onClick={() => setActiveTab("signin")}
              className={`flex-1 py-3.5 sm:py-4 text-sm font-medium transition-all relative ${
                activeTab === "signin" 
                  ? "text-foreground" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-signin"
            >
              Sign In
              {activeTab === "signin" && (
                <motion.div 
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                />
              )}
            </button>
            <button
              onClick={() => setActiveTab("signup")}
              className={`flex-1 py-3.5 sm:py-4 text-sm font-medium transition-all relative ${
                activeTab === "signup" 
                  ? "text-foreground" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-signup"
            >
              Sign Up
              {activeTab === "signup" && (
                <motion.div 
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                />
              )}
            </button>
          </div>

          <div className="p-4 sm:p-6">
            <AnimatePresence mode="wait">
              {activeTab === "signin" ? (
                <motion.div
                  key="signin"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-4 sm:mb-6">
                    <h2 className="text-lg sm:text-xl font-semibold text-foreground">Welcome back</h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">Enter your credentials to continue</p>
                  </div>
                  
                  {/* Google Sign In - Primary option */}
                  <Button 
                    type="button"
                    variant="outline" 
                    className="w-full h-11 sm:h-12 mb-3 sm:mb-4 bg-white hover:bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 dark:border-gray-600" 
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading}
                    data-testid="button-google-signin"
                  >
                    {isGoogleLoading ? (
                      <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                    ) : (
                      <GoogleIcon />
                    )}
                    <span className="ml-3 text-sm sm:text-base">Continue with Google</span>
                  </Button>

                  <div className="relative my-4 sm:my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border/50" />
                    </div>
                    <div className="relative flex justify-center text-[10px] sm:text-xs uppercase">
                      <span className="bg-card px-3 text-muted-foreground">or continue with email</span>
                    </div>
                  </div>

                  <form onSubmit={handleSignIn} className="space-y-3 sm:space-y-4">
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="signin-email" className="text-xs sm:text-sm font-medium">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="signin-email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10 h-11 bg-background/50 border-border/50 focus:border-primary/50"
                          required
                          data-testid="input-signin-email"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="signin-password" className="text-xs sm:text-sm font-medium">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="signin-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10 h-11 bg-background/50 border-border/50 focus:border-primary/50"
                          required
                          data-testid="input-signin-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="remember-me" 
                          checked={rememberMe}
                          onCheckedChange={(checked) => setRememberMe(checked === true)}
                          data-testid="checkbox-remember-me"
                        />
                        <Label 
                          htmlFor="remember-me" 
                          className="text-sm font-normal text-muted-foreground cursor-pointer"
                        >
                          Remember me
                        </Label>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setResetEmail(email);
                          setShowForgotPassword(true);
                        }}
                        className="text-sm text-primary hover:text-primary/80 transition-colors"
                        data-testid="link-forgot-password"
                      >
                        Forgot password?
                      </button>
                    </div>

                    {loginFailed && (
                      <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-muted/50 rounded-xl p-4 space-y-3 border border-border/50"
                      >
                        <p className="text-sm text-muted-foreground text-center font-medium">
                          Did you sign up with Google?
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={handleGoogleSignIn}
                          disabled={isGoogleLoading}
                          data-testid="button-try-google-signin"
                        >
                          {isGoogleLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <GoogleIcon />
                          )}
                          <span className="ml-2">Try Google Sign In</span>
                        </Button>
                        <p className="text-xs text-center text-muted-foreground">
                          Or{" "}
                          <button
                            type="button"
                            onClick={() => {
                              setResetEmail(loginFailedEmail);
                              setShowForgotPassword(true);
                            }}
                            className="text-primary hover:underline"
                            data-testid="link-forgot-password-error"
                          >
                            reset your password
                          </button>
                        </p>
                      </motion.div>
                    )}
                    
                    <Button 
                      type="submit" 
                      className="w-full h-11 font-medium" 
                      disabled={isLoading}
                      data-testid="button-signin"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </Button>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  key="signup"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="mb-4 sm:mb-6">
                    <h2 className="text-lg sm:text-xl font-semibold text-foreground">Create an account</h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">Start your free 14-day trial today</p>
                  </div>

                  {/* Benefits */}
                  <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mb-4 sm:mb-6">
                    <div className="flex flex-col items-center p-2 sm:p-3 rounded-xl bg-primary/5 border border-primary/10">
                      <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary mb-1" />
                      <span className="text-[9px] sm:text-[10px] text-muted-foreground text-center">Free Trial</span>
                    </div>
                    <div className="flex flex-col items-center p-2 sm:p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                      <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500 mb-1" />
                      <span className="text-[9px] sm:text-[10px] text-muted-foreground text-center">No Card</span>
                    </div>
                    <div className="flex flex-col items-center p-2 sm:p-3 rounded-xl bg-violet-500/5 border border-violet-500/10">
                      <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-violet-500 mb-1" />
                      <span className="text-[9px] sm:text-[10px] text-muted-foreground text-center">Full Access</span>
                    </div>
                  </div>

                  {/* Google Sign Up - Primary option */}
                  <Button 
                    type="button"
                    variant="outline" 
                    className="w-full h-11 sm:h-12 mb-3 sm:mb-4 bg-white hover:bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 dark:border-gray-600" 
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading}
                    data-testid="button-google-signup"
                  >
                    {isGoogleLoading ? (
                      <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                    ) : (
                      <GoogleIcon />
                    )}
                    <span className="ml-3 text-sm sm:text-base">Continue with Google</span>
                  </Button>

                  <div className="relative my-4 sm:my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border/50" />
                    </div>
                    <div className="relative flex justify-center text-[10px] sm:text-xs uppercase">
                      <span className="bg-card px-3 text-muted-foreground">or sign up with email</span>
                    </div>
                  </div>

                  <form onSubmit={handleSignUp} className="space-y-3 sm:space-y-4">
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      <div className="space-y-1.5 sm:space-y-2">
                        <Label htmlFor="signup-firstname" className="text-xs sm:text-sm font-medium">First Name</Label>
                        <Input
                          id="signup-firstname"
                          type="text"
                          placeholder="John"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="h-11 bg-background/50 border-border/50 focus:border-primary/50"
                          data-testid="input-signup-firstname"
                        />
                      </div>
                      <div className="space-y-1.5 sm:space-y-2">
                        <Label htmlFor="signup-lastname" className="text-xs sm:text-sm font-medium">Last Name</Label>
                        <Input
                          id="signup-lastname"
                          type="text"
                          placeholder="Doe"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="h-11 bg-background/50 border-border/50 focus:border-primary/50"
                          data-testid="input-signup-lastname"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="signup-email" className="text-xs sm:text-sm font-medium">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10 h-11 bg-background/50 border-border/50 focus:border-primary/50"
                          required
                          data-testid="input-signup-email"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="signup-password" className="text-xs sm:text-sm font-medium">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="signup-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Create a strong password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10 h-11 bg-background/50 border-border/50 focus:border-primary/50"
                          required
                          minLength={6}
                          data-testid="input-signup-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-11 font-medium" 
                      disabled={isLoading}
                      data-testid="button-signup"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating account...
                        </>
                      ) : (
                        "Create Account"
                      )}
                    </Button>
                    
                    <p className="text-xs text-center text-muted-foreground">
                      14-day free trial, then $8.99/month. Cancel anytime.
                    </p>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center text-xs text-muted-foreground mt-6"
        >
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </motion.p>
      </div>

      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Reset your password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="you@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="pl-10 h-11 bg-background/50"
                  required
                  data-testid="input-reset-email"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowForgotPassword(false)}
                data-testid="button-cancel-reset"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isResetLoading}
                data-testid="button-send-reset"
              >
                {isResetLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send reset link"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
